// One streamed Neo turn drawn into a chat's transcript (TOM-731): what each
// event of `POST /v1/neo/chat/stream` (§12.3) does to the messages on screen.
// A typed turn reads its events from its own response; a voice turn reads the
// same events from its call's stream (§12.20.3). Both draw through
// applyNeoStreamEvent, so a spoken question's answer looks exactly like a
// typed one's: its bubble, charts, cards, record and receipts.

import type { Dispatch, SetStateAction } from 'react';
import { parseNeoAnswer, type NeoAnswerChart, type NeoAnswerRecord } from '@neomaris/protocol';

import type { UploadedChatAttachment } from '../../lib/chatAttachments';
import { announceLooksChanged } from '../../lib/boardLooks';
import {
  alarmChangeChip,
  parseAlarmChangeRequests,
  parseAlarmChanges,
  type NeoAlarmChangeRequest,
} from '../../lib/neoAlarmChange';
import { parseAlarmProposals, type NeoAlarmProposal } from '../../lib/neoAlarmProposal';
import { foldNeoAgentEvent, type NeoAgentAtWork } from '../../lib/neoAgentProgress';
import { parseNeoAnswerCards, type NeoAnswerCard } from '../../lib/neoAnswerCards';
import { parseNeoChatCharts } from '../../lib/neoAnswerCharts';
import {
  NEO_BOARD_UPDATED_EVENT,
  announceBoardItemRemoved,
  parseBoardChanges,
  type NeoBoardChange,
} from '../../lib/neoBoard';
import type { NeoAlarmLink } from '../../lib/neoChatHistory';
import type { NeoFeedbackAsk } from '../../lib/neoFeedback';
import { parseLookChanges, type NeoLookReceiptChange } from '../../lib/neoLooksReceipt';
import { NEO_MEMORIES_UPDATED_EVENT, memoryChip, parseMemoryChanges } from '../../lib/neoMemories';
import {
  parseLabelChanges,
  parseLabelRequests,
  type NeoLabelChange,
  type NeoLabelChangeRequest,
} from '../../lib/neoNames';
import { parsePreferenceChanges, preferenceChip } from '../../lib/neoPreferences';
import {
  NEO_RECALL_UPDATED_EVENT,
  parseRecallLookups,
  recallChipMessages,
  recallForgotAny,
} from '../../lib/neoRecall';
import {
  parseDataAnswerChanges,
  parseNotebookChanges,
  type NeoDataAnswerChange,
  type NeoNotebookRecord,
} from '../../lib/neoReceipt';
import type { NeoRequestContext } from '../../lib/neoPanel';
import type { NeoStreamEvent } from '../../lib/neoStream';
import { ALARMS_UPDATED_EVENT } from '../NeoAlarmProposalCard';

/** Fired on window whenever Neo applied a dashboard write — the Instruments
 * page listens and refetches, no shared state needed. */
export const DASHBOARD_UPDATED_EVENT = 'nm:dashboard-updated';

export type ChatRole = 'user' | 'assistant';

export type FeedbackRating = 'up' | 'down';

export interface ChatMessage {
  id: string;
  role: ChatRole;
  content: string;
  local?: boolean;
  // The photos and PDFs an owner message carried (TOM-671, ADR 0269).
  attachments?: UploadedChatAttachment[];
  // An answer the owner stopped part way (TOM-671): what arrived stays, marked.
  stopped?: boolean;
  // An owner's Undo of Neo's change (origin `owner_undo`, TOM-662): a small
  // plain line, the server's text verbatim, not a message from Neo.
  undoNote?: boolean;
  // Printed as typed, never as Markdown: a chip built from stored text the
  // output gate never saw, such as a past conversation's title (TOM-253).
  plain?: boolean;
  // Alarm PROPOSALS reported by the server this turn (ADR 0161). A message
  // carrying these renders confirmation cards instead of a text bubble — the
  // owner's click on one is what creates the alarm. Local and ephemeral: nothing
  // is persisted, so a reload clears them.
  proposals?: NeoAlarmProposal[];
  // LIFE-SAFETY alarm changes awaiting the owner's click (ADR 0210). Rendered as
  // confirmation cards; nothing has changed until the owner confirms one.
  changeRequests?: NeoAlarmChangeRequest[];
  labelRequests?: NeoLabelChangeRequest[];
  // Server message id for an answered assistant turn (ADR 0119) — present only
  // when the turn was persisted, so feedback can be left on it.
  messageId?: string;
  feedback?: FeedbackRating;
  // The ask for what was wrong under a 👎 (TOM-461). Local: a reload brings
  // back the rating only, never the ask.
  feedbackAsk?: NeoFeedbackAsk;
  // The charts this answer shows (ADR 0230), placed in its text by markers.
  charts?: NeoAnswerChart[];
  // The maps (TOM-468), diagrams (TOM-643) and pictures (TOM-646) it shows,
  // placed by `{{card:kN}}` markers. The web draws no other card kind.
  cards?: NeoAnswerCard[];
  // On Neomaris's message about one of the boat's alarms (ADR 0337): the alarm,
  // which "Review alarm" opens (TOM-551).
  alarm?: NeoAlarmLink;
  // Neo's greeting on the boat's home (TOM-920): an answer with chips, never rated.
  greeting?: boolean;
  // Neo's question after Ask Neo on a panel (TOM-958): a line the chat adds
  // itself, never stored, with the panel the owner's reply names (lib/neoPanelQuestion).
  panelQuestion?: NeoRequestContext;
  // What the turn changed on the boat's boards (TOM-354): one receipt per
  // turn, with Open board and Undo. Local and ephemeral, like the other records.
  receipt?: {
    boards: NeoBoardChange[];
    /** Answers about the boat's equipment the turn wrote (TOM-409). */
    answers?: NeoDataAnswerChange[];
    labels?: NeoLabelChange[];
    /** Notes the turn changed in the boat's notebook (TOM-611). */
    notebook?: NeoNotebookRecord[];
  };
  // What the turn changed in the owner's own panel looks (TOM-595, cloud ADR
  // 0364): a receipt of its own, marked "Only you see this", with its own Undo.
  // A board the same turn changed keeps its own receipt.
  lookChanges?: NeoLookReceiptChange[];
  // The answer's record (TOM-709, the answers contract): its brief source line
  // and its follow-ups. Sent to a chat that asks for `answers`, after the
  // answer's cards; read back with the chat.
  answer?: NeoAnswerRecord;
}

/**
 * One turn being drawn: its bubble's id and text, and what its events said.
 * The caller reads `turnRef`, `streamError`, `sawDone` and the ids once the
 * stream ends.
 */
export interface NeoStreamTurn {
  readonly assistantId: string;
  /** The boat the chat is scoped to: a record of another boat is dropped. */
  readonly boatId: string | undefined;
  /** The turn's id from its `turn` frame (§12.3.2): what Stop cancels. */
  turnRef: string | null;
  /** The bubble's text so far, the running source of truth. */
  text: string;
  /** True while the bubble is on screen. */
  shown: boolean;
  streamError: NeoStreamEvent | null;
  /** The turn ended with `done`. A `done` with a null conversation still counts. */
  sawDone: boolean;
  nextConversationId: string | null | undefined;
  nextMessageId: string | undefined;
  /** Cards that wait on the owner (proposals, requests), counted for unique keys. */
  proposalBatch: number;
}

/** What a turn draws with: the chat's own state setters. */
export interface NeoStreamTurnHost {
  setMessages: Dispatch<SetStateAction<ChatMessage[]>>;
  setStreamingId: (id: string | null) => void;
  /** A board the turn made or changed opens beside the chat page (TOM-357). */
  setLiveReceiptId: (id: string) => void;
  setAgentsAtWork: Dispatch<SetStateAction<readonly NeoAgentAtWork[]>>;
  /** Read the account again: Neo changed the owner's settings. */
  refreshAccount: () => void;
}

export function newNeoStreamTurn(assistantId: string, boatId: string | undefined): NeoStreamTurn {
  return {
    assistantId,
    boatId,
    turnRef: null,
    text: '',
    shown: false,
    streamError: null,
    sawDone: false,
    nextConversationId: undefined,
    nextMessageId: undefined,
    proposalBatch: 0,
  };
}

/**
 * The streamed bubble is created lazily on the first text so the "thinking"
 * indicator shows until a token arrives.
 */
export function showNeoTurnBubble(turn: NeoStreamTurn, host: NeoStreamTurnHost, next: string) {
  turn.text = next;
  const { assistantId } = turn;
  if (!turn.shown) {
    turn.shown = true;
    host.setStreamingId(assistantId);
    // Above the turn's records, should any have arrived first (a `reset`
    // took the bubble away), so the receipt stays under Neo's words.
    host.setMessages((prev) => {
      const bubble: ChatMessage = { id: assistantId, role: 'assistant', content: next };
      const at = prev.findIndex((m) => m.id.startsWith(`${assistantId}-`));
      return at < 0 ? [...prev, bubble] : [...prev.slice(0, at), bubble, ...prev.slice(at)];
    });
    return;
  }
  host.setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, content: next } : m)));
}

/**
 * Revert to the pre-bubble state (on a `reset`): drop the partial bubble so
 * the "thinking" indicator returns until the real answer's tokens arrive,
 * instead of leaving a blank assistant bubble during the tool round.
 */
export function hideNeoTurnBubble(turn: NeoStreamTurn, host: NeoStreamTurnHost) {
  turn.text = '';
  if (!turn.shown) return;
  turn.shown = false;
  host.setStreamingId(null);
  host.setMessages((prev) => prev.filter((m) => m.id !== turn.assistantId));
}

/**
 * The turn's board, equipment and name records gather in one receipt
 * (TOM-354), however many frames carry them.
 */
function addToNeoTurnReceipt(
  turn: NeoStreamTurn,
  host: NeoStreamTurnHost,
  boards: readonly NeoBoardChange[],
  answers: readonly NeoDataAnswerChange[] = [],
  labels: readonly NeoLabelChange[] = [],
  notebook: readonly NeoNotebookRecord[] = []
) {
  const receiptId = `${turn.assistantId}-receipt`;
  // A board this turn made or changed opens beside the chat page (TOM-357).
  if (boards.length > 0) host.setLiveReceiptId(receiptId);
  host.setMessages((prev) => {
    const at = prev.findIndex((m) => m.id === receiptId);
    const was = at < 0 ? undefined : prev[at]!.receipt;
    const receipt = {
      boards: [...(was?.boards ?? []), ...boards],
      answers: [...(was?.answers ?? []), ...answers],
      labels: [...(was?.labels ?? []), ...labels],
      notebook: [...(was?.notebook ?? []), ...notebook],
    };
    if (at < 0) {
      return [
        ...prev,
        { id: receiptId, role: 'assistant' as const, content: '', local: true, receipt },
      ];
    }
    const next = [...prev];
    next[at] = { ...prev[at]!, receipt };
    return next;
  });
}

/** Draw one event of a Neo turn's stream (§12.3) into the chat. */
export function applyNeoStreamEvent(
  turn: NeoStreamTurn,
  host: NeoStreamTurnHost,
  event: string,
  data: NeoStreamEvent
): void {
  switch (event) {
    case 'turn':
      // The turn's id, for Stop (§12.3.2).
      if (typeof data.turnRef === 'string') turn.turnRef = data.turnRef;
      break;
    case 'delta':
      showNeoTurnBubble(turn, host, turn.text + (data.text ?? ''));
      break;
    case 'reset':
      hideNeoTurnBubble(turn, host);
      break;
    case 'replace':
      showNeoTurnBubble(turn, host, data.content ?? '');
      break;
    case 'error':
      turn.streamError = data;
      break;
    case 'mutations': {
      // The server's record of APPLIED dashboard writes — truthful even
      // when the narration is later suppressed. Chip + page refetch.
      const applied = data.mutations ?? [];
      if (applied.length > 0) {
        const label =
          applied.length === 1
            ? `Dashboard updated${applied[0]!.version !== null ? ` (v${applied[0]!.version})` : ''}: ${applied[0]!.summary}`
            : `Dashboard updated — ${applied.map((m) => m.summary).join('; ')}`;
        host.setMessages((prev) => [
          ...prev,
          {
            id: `${turn.assistantId}-mutation`,
            role: 'assistant',
            content: `✓ ${label}`,
            local: true,
          },
        ]);
        window.dispatchEvent(new CustomEvent(DASHBOARD_UPDATED_EVENT));
      }
      break;
    }
    case 'alarm_proposal': {
      // DRAFTS, not writes: nothing exists until the owner clicks a card
      // (ADR 0161). Appended as its own local transcript entry — one card
      // per proposal, in the order the server reported them. On a boat
      // page this chat is scoped, so a proposal stamped with any other
      // boat is dropped at decode; with no boatId (the general chat,
      // legitimately multi-boat) nothing is filtered.
      const proposals = parseAlarmProposals(data.proposals, turn.boatId);
      if (proposals.length > 0) {
        host.setMessages((prev) => [
          ...prev,
          {
            id: `${turn.assistantId}-proposal-${turn.proposalBatch++}`,
            role: 'assistant',
            content: '',
            local: true,
            proposals,
          },
        ]);
      }
      break;
    }
    case 'alarm_changes': {
      // The server's record of alarm changes Neo MADE (ADR 0210) —
      // truthful even when the narration is later suppressed. One chip
      // per change, and the Alarms page refetches.
      const changes = parseAlarmChanges(data.changes, turn.boatId);
      if (changes.length > 0) {
        host.setMessages((prev) => [
          ...prev,
          ...changes.map((change, i) => ({
            id: `${turn.assistantId}-alarm-change-${i}`,
            role: 'assistant' as const,
            content: alarmChangeChip(change),
            local: true,
          })),
        ]);
        window.dispatchEvent(new CustomEvent(ALARMS_UPDATED_EVENT));
      }
      break;
    }
    case 'alarm_change_requests': {
      // Life-safety changes: nothing has changed until the owner clicks
      // a card (ADR 0210). Decoded like proposals — a request for another
      // boat is dropped on a boat-scoped page.
      const changeRequests = parseAlarmChangeRequests(data.requests, turn.boatId);
      if (changeRequests.length > 0) {
        host.setMessages((prev) => [
          ...prev,
          {
            id: `${turn.assistantId}-alarm-change-request-${turn.proposalBatch++}`,
            role: 'assistant',
            content: '',
            local: true,
            changeRequests,
          },
        ]);
      }
      break;
    }
    case 'preferences': {
      // Account settings Neo CHANGED (ADR 0225) — units today. One chip
      // each, then refresh the account so every page beside this chat
      // re-renders in the new units.
      const changes = parsePreferenceChanges(data.changes);
      if (changes.length > 0) {
        host.setMessages((prev) => [
          ...prev,
          ...changes.map((change, i) => ({
            id: `${turn.assistantId}-preference-${i}`,
            role: 'assistant' as const,
            content: preferenceChip(change),
            local: true,
          })),
        ]);
        host.refreshAccount();
      }
      break;
    }
    case 'memories': {
      // Notes Neo saved, replaced or forgot (ADR 0226) — shown as chips so
      // the owner sees exactly what is kept, whatever the narration says.
      const changes = parseMemoryChanges(data.changes);
      if (changes.length > 0) {
        host.setMessages((prev) => [
          ...prev,
          ...changes.map((change, i) => ({
            id: `${turn.assistantId}-memory-${i}`,
            role: 'assistant' as const,
            content: memoryChip(change),
            local: true,
          })),
        ]);
        window.dispatchEvent(new CustomEvent(NEO_MEMORIES_UPDATED_EVENT));
      }
      break;
    }
    case 'recall': {
      // The owner's past conversations this turn searched, opened or
      // forgot (TOM-253) — a chip each, whatever the narration says, so
      // the owner sees what Neo looked at in their history. A forget also
      // makes an open Profile drop the summary.
      const lookups = parseRecallLookups(data.lookups);
      const chips = recallChipMessages(lookups, turn.assistantId);
      if (chips.length > 0) host.setMessages((prev) => [...prev, ...chips]);
      if (recallForgotAny(lookups)) {
        window.dispatchEvent(new CustomEvent(NEO_RECALL_UPDATED_EVENT));
      }
      break;
    }
    case 'board': {
      // What Neo changed on the boat's boards (ADR 0291 §8): the turn's
      // receipt, with Open board and Undo (TOM-354), whatever the
      // narration says. An open Board page refetches.
      const changes = parseBoardChanges(data.changes);
      if (changes.length > 0) {
        addToNeoTurnReceipt(turn, host, changes);
        window.dispatchEvent(new CustomEvent(NEO_BOARD_UPDATED_EVENT));
        for (const change of changes) {
          if (change.action === 'unpinned' && change.itemId !== null) {
            announceBoardItemRemoved(change.itemId);
          }
        }
      }
      break;
    }
    case 'looks': {
      // Neo changed the owner's own panel looks (TOM-594, cloud ADR
      // 0364): a private receipt under the answer, apart from any board
      // receipt, with its own Undo. An open board reads
      // the looks again. A payload this chat can't read is no receipt.
      const changes = parseLookChanges(data.changes);
      if (changes.length > 0) {
        const looksId = `${turn.assistantId}-looks`;
        host.setMessages((prev) => {
          const at = prev.findIndex((m) => m.id === looksId);
          if (at < 0) {
            return [
              ...prev,
              {
                id: looksId,
                role: 'assistant' as const,
                content: '',
                local: true,
                lookChanges: changes,
              },
            ];
          }
          const next = [...prev];
          next[at] = {
            ...prev[at]!,
            lookChanges: [...(prev[at]!.lookChanges ?? []), ...changes],
          };
          return next;
        });
        window.dispatchEvent(new CustomEvent(NEO_BOARD_UPDATED_EVENT));
        for (const boat of new Set(changes.map((c) => c.boatId))) announceLooksChanged(boat);
      }
      break;
    }
    case 'label_requests': {
      const labelRequests = parseLabelRequests(data.requests, turn.boatId);
      if (labelRequests.length > 0)
        host.setMessages((prev) => [
          ...prev,
          {
            id: `${turn.assistantId}-label-requests-${turn.proposalBatch++}`,
            role: 'assistant',
            content: '',
            local: true,
            labelRequests,
          },
        ]);
      break;
    }
    case 'label_changes': {
      const changes = parseLabelChanges(data.changes, turn.boatId);
      if (changes.length > 0) addToNeoTurnReceipt(turn, host, [], [], changes);
      break;
    }
    case 'data_answers': {
      // Neo wrote the owner's answers about what is aboard (ADR 0312 §9,
      // TOM-408): a part of the same receipt, with its own Undo.
      const changes = parseDataAnswerChanges(data.changes);
      if (changes.length > 0) addToNeoTurnReceipt(turn, host, [], changes);
      break;
    }
    case 'notebook': {
      // Neo changed the boat's notebook (TOM-611, cloud ADR 0374): "Neo
      // noted for <boat>", shared with everyone on the boat, undone with
      // the whole request. On a boat page a note on another boat is dropped.
      const changes = parseNotebookChanges(data.changes).filter(
        (c) => turn.boatId === undefined || c.boatId === turn.boatId
      );
      if (changes.length > 0) addToNeoTurnReceipt(turn, host, [], [], [], changes);
      break;
    }
    case 'charts': {
      // The charts a valid answer shows (ADR 0230), after its final text.
      // On a boat page a chart of another boat is dropped at decode.
      const charts = parseNeoChatCharts(data.charts, data.answerCharts, turn.boatId);
      if (charts.length > 0) {
        host.setMessages((prev) =>
          prev.map((m) => (m.id === turn.assistantId ? { ...m, charts } : m))
        );
      }
      break;
    }
    case 'cards': {
      // The maps (TOM-468), diagrams (TOM-643) and pictures (TOM-646) a
      // valid answer shows, on the charts' terms: another card kind, or
      // a card of another boat, is dropped.
      const cards = parseNeoAnswerCards(data.cards, turn.boatId);
      if (cards.length > 0) {
        host.setMessages((prev) =>
          prev.map((m) => (m.id === turn.assistantId ? { ...m, cards } : m))
        );
      }
      break;
    }
    case 'answer': {
      // A valid answer's record (TOM-709), after its cards: the source
      // line and the follow-ups. One the chat cannot read is no record.
      const answer = parseNeoAnswer(data.answer);
      if (answer !== null) {
        host.setMessages((prev) =>
          prev.map((m) => (m.id === turn.assistantId ? { ...m, answer } : m))
        );
      }
      break;
    }
    case 'agent':
      // A specialist Neo consults started or ended (TOM-543): the busy
      // row names what Neo is checking, never a value.
      host.setAgentsAtWork((prev) => foldNeoAgentEvent(prev, data));
      break;
    case 'done':
      turn.sawDone = true;
      turn.nextConversationId = data.conversationId ?? undefined;
      turn.nextMessageId = data.messageId ?? undefined;
      break;
  }
}
